import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from '@app/core/services/API/api-service';
import {
  downloadFileKeepingExtension,
  ensureFileNameHasExtension,
  getFileExtensionFromNameOrUrl,
  isImageFileNameOrUrl,
  saveBlobAsFile,
} from '@app/core/services/html-processing/html-processing.service';

const MEDIA_FILE_EXTENSIONS = [
  'mp4',
  'webm',
  'ogg',
  'avi',
  'mov',
  'mkv',
  'm4v',
  'wmv',
  'mp3',
  'wav',
  'm4a',
  'aac',
] as const;

const VIDEO_FILE_EXTENSIONS = [
  'mp4',
  'webm',
  'ogg',
  'avi',
  'mov',
  'mkv',
  'm4v',
  'wmv',
] as const;

export type NotificationAttachmentKind = 'image' | 'media' | 'file';

/**
 * Shared image/media preview + extension-preserving download for
 * app-notifications and app-notification-dropdown (same behavior as documents).
 */
@Injectable({ providedIn: 'root' })
export class NotificationAttachmentPreviewService implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly toastr = inject(ToastrService);
  private readonly destroy$ = new Subject<void>();

  readonly isImageModalOpen = signal(false);
  readonly selectedImageUrl = signal('');
  readonly selectedImageTitle = signal('');
  readonly selectedImageSize = signal('');
  readonly selectedImageDownloadUrl = signal('');
  readonly selectedImageUploadedAt = signal<string | Date | null>(null);

  readonly isMediaModalOpen = signal(false);
  readonly selectedMediaUrl = signal('');
  readonly selectedMediaTitle = signal('');
  readonly selectedMediaSize = signal('');
  readonly selectedMediaType = signal<'audio' | 'video'>('video');
  readonly selectedMediaWebViewLink = signal('');

  readonly downloadProgress = signal(0);
  private fakeProgressTimer: ReturnType<typeof setInterval> | null = null;

  ngOnDestroy(): void {
    this.clearFakeProgress();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Open preview modal or start download based on file type. */
  openAttachment(
    url: string,
    fileName: string,
    preferredKind?: NotificationAttachmentKind
  ): void {
    const href = (url || '').trim();
    if (!href || href === '#') {
      return;
    }

    const resolvedName = ensureFileNameHasExtension(
      (fileName || '').trim() || this.basenameFromHref(href) || 'download',
      href
    );
    const kind =
      preferredKind ||
      this.detectKind(resolvedName, href, fileName);

    if (kind === 'image') {
      this.openImagePreview(href, resolvedName);
      return;
    }
    if (kind === 'media') {
      const ext = getFileExtensionFromNameOrUrl(resolvedName, href);
      this.openMediaPreview(href, resolvedName, ext);
      return;
    }

    this.download(href, resolvedName, false);
  }

  detectKind(
    resolvedName: string,
    url: string,
    rawName = ''
  ): NotificationAttachmentKind {
    if (
      isImageFileNameOrUrl(resolvedName, url) ||
      isImageFileNameOrUrl(rawName, url)
    ) {
      return 'image';
    }
    const ext = getFileExtensionFromNameOrUrl(resolvedName, url);
    if ((MEDIA_FILE_EXTENSIONS as readonly string[]).includes(ext)) {
      return 'media';
    }
    return 'file';
  }

  isMediaExtension(ext: string): boolean {
    return (MEDIA_FILE_EXTENSIONS as readonly string[]).includes(
      (ext || '').toLowerCase()
    );
  }

  openImagePreview(url: string, fileName: string): void {
    this.closeMediaModal();
    this.selectedImageUrl.set(url);
    this.selectedImageTitle.set(ensureFileNameHasExtension(fileName, url));
    this.selectedImageDownloadUrl.set(url);
    this.selectedImageSize.set('');
    this.selectedImageUploadedAt.set(null);
    this.downloadProgress.set(0);
    this.isImageModalOpen.set(true);
  }

  openMediaPreview(url: string, fileName: string, fileType: string): void {
    this.closeImageModal();
    const ext = (fileType || getFileExtensionFromNameOrUrl(fileName, url)).toLowerCase();
    const isVideo = (VIDEO_FILE_EXTENSIONS as readonly string[]).includes(ext);
    this.selectedMediaUrl.set(url);
    this.selectedMediaTitle.set(ensureFileNameHasExtension(fileName, url));
    this.selectedMediaType.set(isVideo || !ext ? 'video' : 'audio');
    this.selectedMediaSize.set('');
    this.selectedMediaWebViewLink.set('');
    this.downloadProgress.set(0);
    this.isMediaModalOpen.set(true);
  }

  closeImageModal(): void {
    this.isImageModalOpen.set(false);
    this.selectedImageUrl.set('');
    this.selectedImageTitle.set('');
    this.selectedImageSize.set('');
    this.selectedImageDownloadUrl.set('');
    this.selectedImageUploadedAt.set(null);
    this.clearDownloadProgress();
  }

  closeMediaModal(): void {
    this.isMediaModalOpen.set(false);
    this.selectedMediaUrl.set('');
    this.selectedMediaTitle.set('');
    this.selectedMediaSize.set('');
    this.selectedMediaWebViewLink.set('');
    this.clearDownloadProgress();
  }

  onImageDownload(downloadUrl: string): void {
    const url = (downloadUrl || this.selectedImageDownloadUrl() || this.selectedImageUrl()).trim();
    const name = this.selectedImageTitle() || 'image';
    this.download(url, name, true);
  }

  onMediaDownload(downloadUrl: string): void {
    const url = (downloadUrl || this.selectedMediaUrl()).trim();
    const name = this.selectedMediaTitle() || 'media';
    this.download(url, name, true);
  }

  onImageShare(imageUrl: string): void {
    this.shareUrl(imageUrl, this.selectedImageTitle());
  }

  onMediaShare(mediaUrl: string): void {
    this.shareUrl(mediaUrl, this.selectedMediaTitle());
  }

  /**
   * Download with extension restored from URL / Content-Disposition / MIME
   * (same path as documents & updates).
   */
  download(url: string, fileName: string, trackProgress = false): void {
    const resolvedName = ensureFileNameHasExtension(fileName, url);
    if (!url || !String(url).trim()) {
      this.toastr.error('Download link is missing or invalid.');
      return;
    }

    if (trackProgress) {
      this.setProgress(1);
    }

    this.api
      .downloadRemoteFileWithProgress(url)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (evt) => {
          if (trackProgress) {
            this.setProgress(evt.progress);
          }
          if (evt.blob) {
            const saved = saveBlobAsFile(evt.blob, resolvedName, url);
            if (saved) {
              this.toastr.success(`${resolvedName} downloaded successfully`);
              if (trackProgress) {
                this.finishProgress();
              }
            } else {
              this.toastr.error(`Failed to download ${resolvedName}. Please try again.`);
              if (trackProgress) {
                this.clearDownloadProgress();
              }
            }
          }
        },
        error: (err: { status?: number } | null) => {
          if (err?.status === 401) {
            if (trackProgress) {
              this.clearDownloadProgress();
            }
            return;
          }
          if (trackProgress) {
            this.startFakeProgress();
          }
          void downloadFileKeepingExtension(url, resolvedName)
            .then((ok) => {
              if (ok) {
                this.toastr.success(`${resolvedName} downloaded successfully`);
                if (trackProgress) {
                  this.finishProgress();
                }
              } else {
                this.toastr.error(`Failed to download ${resolvedName}. Please try again.`);
                if (trackProgress) {
                  this.clearDownloadProgress();
                }
              }
            })
            .catch(() => {
              this.toastr.error(`Failed to download ${resolvedName}. Please try again.`);
              if (trackProgress) {
                this.clearDownloadProgress();
              }
            });
        },
      });
  }

  basenameFromHref(href: string): string {
    if (!href) {
      return '';
    }
    try {
      const u = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
      return decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() || '') || '';
    } catch {
      const parts = href.split(/[/?#]/).filter(Boolean);
      return parts.length ? decodeURIComponent(parts[parts.length - 1]) : '';
    }
  }

  private shareUrl(url: string, title: string): void {
    if (!url) {
      return;
    }
    if (navigator.share) {
      void navigator.share({ title: title || 'Attachment', url }).catch(() => undefined);
      return;
    }
    void navigator.clipboard?.writeText(url).catch(() => undefined);
  }

  private setProgress(progress: number): void {
    this.downloadProgress.set(Math.max(0, Math.min(100, Math.round(progress))));
  }

  private startFakeProgress(): void {
    this.clearFakeProgress();
    this.fakeProgressTimer = setInterval(() => {
      const current = this.downloadProgress();
      if (current >= 99) {
        return;
      }
      this.setProgress(Math.min(99, current + Math.max(1, Math.round((99 - current) * 0.12))));
    }, 180);
  }

  private clearFakeProgress(): void {
    if (this.fakeProgressTimer) {
      clearInterval(this.fakeProgressTimer);
      this.fakeProgressTimer = null;
    }
  }

  private finishProgress(): void {
    this.clearFakeProgress();
    this.setProgress(100);
    window.setTimeout(() => this.clearDownloadProgress(), 450);
  }

  private clearDownloadProgress(): void {
    this.clearFakeProgress();
    this.downloadProgress.set(0);
  }
}
